import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';
function placeTooltip(rect, side, gap = 8) {
    if (side === 'left') {
        return { top: rect.top + rect.height / 2, left: rect.left - gap };
    }
    if (side === 'top') {
        return { top: rect.top - gap, left: rect.left + rect.width / 2 };
    }
    if (side === 'bottom') {
        return { top: rect.bottom + gap, left: rect.left + rect.width / 2 };
    }
    return { top: rect.top + rect.height / 2, left: rect.right + gap };
}
const transformClass = {
    right: '-translate-y-1/2',
    left: '-translate-y-1/2 -translate-x-full',
    top: '-translate-x-1/2 -translate-y-full',
    bottom: '-translate-x-1/2',
};
function getPortalRoot() {
    if (typeof document === 'undefined')
        return null;
    const existing = document.getElementById('unity-tooltip-root');
    if (existing)
        return existing;
    const root = document.createElement('div');
    root.id = 'unity-tooltip-root';
    root.setAttribute('data-tooltip-portal', 'true');
    document.body.appendChild(root);
    return root;
}
export function Tooltip({ label, side = 'right', children, className, }) {
    const triggerRef = useRef(null);
    const tooltipId = useId();
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [portalRoot, setPortalRoot] = useState(null);
    useEffect(() => {
        setPortalRoot(getPortalRoot());
    }, []);
    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el)
            return;
        setCoords(placeTooltip(el.getBoundingClientRect(), side));
    }, [side]);
    useLayoutEffect(() => {
        if (!open)
            return;
        updatePosition();
    }, [open, updatePosition]);
    useEffect(() => {
        if (!open)
            return;
        const onReposition = () => updatePosition();
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);
        return () => {
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [open, updatePosition]);
    const show = () => {
        updatePosition();
        setOpen(true);
    };
    const hide = () => setOpen(false);
    return (_jsxs("span", { ref: triggerRef, className: cn('inline-flex', className), onMouseEnter: show, onMouseLeave: hide, onFocusCapture: show, onBlurCapture: hide, "aria-describedby": open ? tooltipId : undefined, children: [children, open && portalRoot
                ? createPortal(_jsx("span", { id: tooltipId, role: "tooltip", className: cn('pointer-events-none fixed z-[200] whitespace-nowrap rounded-md border border-border bg-foreground px-2.5 py-1.5 text-xs font-medium text-background shadow-md', transformClass[side]), style: { top: coords.top, left: coords.left }, children: label }), portalRoot)
                : null] }));
}
